import { useState } from 'react'
import toast from 'react-hot-toast'
import { ArrowRight, MessageSquare, LayoutList, Users } from 'lucide-react'
import { useJourneyStore, refreshJourney } from '@/store/journeyStore'
import { useProjectStore } from '@/store/projectStore'
import { useJourneyAction } from '@/lib/journeyActions'
import { skipStep } from '@/api/journey'
import { TYPE_ICON, typeName, statusLabel } from '@/components/workbench/AppTree'
import JourneyBar from './JourneyBar'

/**
 * Next step — the journey, ONE next action, "Your app so far" and the things
 * you can do any time. Everything comes from the journey resolver; this
 * component only draws it (plan §4).
 */
export default function NextStepTab() {
  const { journey, loading, error, refresh } = useJourneyStore()
  const project = useProjectStore((s) => s.currentProject?.application.folder_name || '')
  const run = useJourneyAction()
  const [skipping, setSkipping] = useState(false)

  if (!journey) {
    return (
      <div className="swb swb-page">
        {error ? (
          <div className="swb-notice swb-notice-warn">
            <span style={{ flex: 1 }}>{error}</span>
            <button type="button" className="swb-btn swb-btn-secondary" onClick={() => project && void refresh(project)}>Try again</button>
          </div>
        ) : loading ? <div className="spinner" style={{ margin: '3rem auto' }} /> : null}
      </div>
    )
  }

  const next = journey.next
  const objTab = journey.tabs.find((t) => t.view === 'objects')
  const nouns = objTab?.nouns || 'screens'

  const skip = async () => {
    if (!next.skip || !project) return
    setSkipping(true)
    try {
      await skipStep(project, next.skip.step, true)
      refreshJourney()
    } catch (err) {
      console.error('[journey] skip failed', err)
      toast.error('Could not skip this step')
    } finally {
      setSkipping(false)
    }
  }

  return (
    <div className="swb swb-page">
      <JourneyBar steps={journey.steps} />

      <section className="swb-card swb-hero" aria-labelledby="next-title">
        {next.eyebrow && <span className="swb-eyebrow">{next.eyebrow}</span>}
        <h1 id="next-title" style={{ margin: 0, fontSize: '1.875rem', lineHeight: 1.2, fontWeight: 600, letterSpacing: '-.01em' }}>
          {next.title}
        </h1>
        {next.body && <p style={{ margin: 0, fontSize: '1rem', maxWidth: 640 }}>{next.body}</p>}
        {typeof next.progress === 'number' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="swb-progress" role="progressbar" aria-valuenow={next.progress} aria-valuemin={0} aria-valuemax={100}>
              <div style={{ width: `${next.progress}%` }} />
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', marginTop: 4 }}>
          {next.primary && (
            <button type="button" className="swb-btn swb-btn-primary swb-btn-lg" onClick={() => void run(next.primary!.action)} data-tw-action="next-primary">
              {next.primary.label} <ArrowRight size={18} />
            </button>
          )}
          {next.secondary && (
            <button type="button" className="swb-btn swb-btn-secondary swb-btn-lg" onClick={() => void run(next.secondary!.action)}>
              {next.secondary.label}
            </button>
          )}
          {next.skip && (
            <button type="button" className="swb-link" onClick={() => void skip()} disabled={skipping}>
              {next.skip.label}
            </button>
          )}
          {next.meta && <span className="swb-muted">{next.meta}</span>}
        </div>
      </section>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        <section className="swb-card" aria-labelledby="sofar" style={{ flex: '2 1 420px', overflow: 'hidden' }}>
          <div className="swb-band">
            <h2 id="sofar">Your app so far</h2>
            <span className="swb-muted">
              {journey.counts.planned
                ? `${journey.counts.built} of ${journey.counts.planned} ready · ${journey.counts.checked} checked`
                : 'Nothing yet'}
            </span>
          </div>
          {journey.objects.length === 0 ? (
            <p className="swb-muted" style={{ margin: 0, padding: '22px 18px' }}>
              No {nouns} yet. Once you describe your app, they appear here as the AI plans and builds them.
            </p>
          ) : (
            <ul className="swb-list" style={{ maxHeight: 420, overflow: 'auto' }}>
              {journey.objects.map((o) => {
                const Icon = TYPE_ICON[o.obj_type] || TYPE_ICON.T
                return (
                  <li key={`${o.obj_type}:${o.obj_name}`}>
                    <Icon size={18} style={{ color: 'var(--color-accent)', flex: 'none' }} />
                    <span style={{ flex: '1 1 160px', fontSize: 14 }}>{o.title}</span>
                    <span className="swb-muted" style={{ flex: '0 0 130px' }}>{typeName(o.obj_type)}</span>
                    <span className={`swb-chip swb-chip-${o.status}`}>{statusLabel(o.status)}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section aria-labelledby="anytime" style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 id="anytime" className="swb-section-title">Any time</h2>
          <button type="button" className="swb-row-btn" onClick={() => void run('ask:')}>
            <MessageSquare size={20} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 2 }} />
            <span><strong>Ask for a change</strong><span className="swb-muted">Say it in your own words — the AI explains its plan first</span></span>
          </button>
          {objTab && (
            <button type="button" className="swb-row-btn" onClick={() => void run(`tab:${objTab.id}`)}>
              <LayoutList size={20} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 2 }} />
              <span><strong>Look at the {nouns}</strong><span className="swb-muted">Try anything that is ready</span></span>
            </button>
          )}
          <button type="button" className="swb-row-btn" onClick={() => void run('open:people_invite')}>
            <Users size={20} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 2 }} />
            <span><strong>Invite people</strong><span className="swb-muted">Teammates can help build or try it</span></span>
          </button>
        </section>
      </div>
    </div>
  )
}
